import React, { useEffect } from 'react'
import { capitalize } from 'lodash-es'
import { UseFormClearErrors, UseFormRegister, UseFormSetError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Combobox, Input } from '@node-space/ui-components'
import {
  CryptoPayout,
  UpdateCryptoPayoutFormPayload,
  UpdateCryptoPayoutFormPayloadKeys,
} from 'pages/MassPayouts/types'
import { Currency } from 'types/types'
import { formatErrorMsg, mapToItems } from 'utils/utils'

interface CryptoPayoutEditRecordFormProps {
  massPayoutEditRecordProps: Partial<UpdateCryptoPayoutFormPayload>
  errors: Partial<Record<UpdateCryptoPayoutFormPayloadKeys, { message?: string; code?: string }>>
  handleOnChange: (field: UpdateCryptoPayoutFormPayloadKeys, value: string) => void
  register: UseFormRegister<UpdateCryptoPayoutFormPayload>
  setError: UseFormSetError<UpdateCryptoPayoutFormPayload>
  payout: CryptoPayout
  walletName: string
  walletCurrency: string
  validateCryptoPayout: (field: string) => void
  clearErrors: UseFormClearErrors<UpdateCryptoPayoutFormPayload>
  cryptoCurrencies: Currency[]
  isFormDisabled?: boolean
}

export const CryptoPayoutEditRecordForm = ({
  massPayoutEditRecordProps,
  errors,
  handleOnChange,
  register,
  setError,
  payout,
  walletName,
  walletCurrency,
  validateCryptoPayout,
  clearErrors,
  cryptoCurrencies,
}: CryptoPayoutEditRecordFormProps) => {
  const { t } = useTranslation()

  const cryptoCurrenciesOptions = mapToItems(cryptoCurrencies)

  const protocols = cryptoCurrencies
    ?.filter(currency => currency?.code === massPayoutEditRecordProps?.currency)
    ?.map(currency => currency?.protocols)
    ?.flat()

  const protocolOptions = protocols?.map(protocol => ({
    value: protocol?.code,
    leftText: protocol?.network,
  }))

  const networkOptions = protocols?.map(protocol => ({
    value: protocol?.networkCode,
    leftText: protocol?.network,
  }))

  const errorMessages = formatErrorMsg(payout?.errorMessage?.value)

  useEffect(() => {
    if (protocolOptions?.length === 1) {
      clearErrors('protocol')
    }

    if (networkOptions?.length === 1) {
      clearErrors('network')
    }
  }, [massPayoutEditRecordProps?.currency])

  useEffect(() => {
    errorMessages?.errorList.map(error => {
      const code = error?.code
      const message = capitalize(error?.message)

      switch (code) {
        case 'MER-PAY-2019':
        case 'MER-PAY-2011':
          setError('currency', { message, type: code })
          break
        case 'MER-PAY-2024':
        case 'MER-PAY-2025':
          setError('protocol', { message, type: code })
          setError('network', { message, type: code })
          break
        case 'MER-PAY-2001':
        case 'MER-PAY-2012':
          setError('cryptoAmount', { message, type: code })
          break
        case 'MER-PAY-2009':
          setError('reference', { message, type: code })
          break
        case 'MER-PAY-2027':
        case 'MER-PAY-2028':
          setError('recipientAddress', { message, type: code })
          break
        case 'MER-PAY-2048':
        case 'MER-PAY-2049':
        case 'MER-PAY-2050':
          setError('travelRule', { message, type: code })
          break
      }
    })
  }, [setError, payout])

  const selectedFundingWalletLabel = `${walletName} (${walletCurrency})`

  return (
    <>
      <Combobox
        id={'payin-from-funding-wallet'}
        label={t('massPayouts.payingFromFundingWallet')}
        defaultValue={walletCurrency}
        items={[
          {
            LeftElement: <Avatar name={walletCurrency?.toLowerCase()} size="small" />,
            value: walletCurrency,
            leftText: selectedFundingWalletLabel,
          },
        ]}
        disabled
      />
      {payout?.fundingWalletAmount ? (
        <Input
          testId="update-amount-input"
          {...register('cryptoAmount')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange('cryptoAmount', e.target.value)
          }
          label={t('massPayouts.fundingWalletAmountDebit')}
          inputMode="text"
          name="cryptoAmount"
          value={massPayoutEditRecordProps?.cryptoAmount}
          error={!!errors?.cryptoAmount}
          errorText={errors?.cryptoAmount?.message}
          disabled={!errors?.cryptoAmount}
          onBlur={() => validateCryptoPayout('cryptoAmount')}
        />
      ) : (
        <Input
          label={t('massPayouts.fundingWalletAmountDebit')}
          inputMode="text"
          name="cryptoAmountLabel"
          value={t('massPayouts.debitedFromFundingWalletWillBeCalculate', {
            currencyCode: walletCurrency,
          })}
          disabled
        />
      )}
      <Combobox
        id="currency"
        {...register('currency')}
        label={t('massPayouts.currencyCryptoLabel')}
        defaultValue={massPayoutEditRecordProps?.currency}
        items={cryptoCurrenciesOptions}
        errorText={errors?.currency?.message}
        searchPlaceholder={t('searchOrSelectACurrency')}
        disabled={!errors?.currency}
        onChange={value => {
          handleOnChange('currency', value)
        }}
      />
      {payout?.fundingWalletAmount ? (
        <Input
          testId="update-amount-input"
          label={t('massPayouts.amountCryptoLabel')}
          inputMode="text"
          name="cryptoAmountLabel"
          value={t('massPayouts.amountWillCalculateAtPayment', {
            currencyCode: massPayoutEditRecordProps?.currency,
          })}
          disabled
        />
      ) : (
        <Input
          testId="update-amount-input"
          {...register('cryptoAmount')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange('cryptoAmount', e.target.value)
          }
          label={t('massPayouts.amountCryptoLabel')}
          inputMode="text"
          name="cryptoAmount"
          value={massPayoutEditRecordProps?.cryptoAmount}
          error={!!errors?.cryptoAmount}
          errorText={errors?.cryptoAmount?.message}
          disabled={!errors?.cryptoAmount}
          onBlur={() => validateCryptoPayout('cryptoAmount')}
        />
      )}
      {massPayoutEditRecordProps?.network && (
        <Combobox
          id="network"
          {...register('network')}
          label={t('massPayouts.network')}
          items={networkOptions}
          defaultValue={massPayoutEditRecordProps?.network}
          errorText={errors?.network?.message}
          onChange={value => {
            handleOnChange('network', value)
          }}
          disabled={!errors?.network}
        />
      )}
      {massPayoutEditRecordProps?.protocol && (
        <Combobox
          id="network"
          {...register('protocol')}
          label={t('massPayouts.blockchainProtocol')}
          items={protocolOptions}
          defaultValue={massPayoutEditRecordProps?.protocol}
          errorText={errors?.protocol?.message}
          onChange={value => {
            handleOnChange('protocol', value)
          }}
          disabled={!errors?.protocol}
        />
      )}
      <Input
        testId="update-address-input"
        {...register('recipientAddress')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleOnChange('recipientAddress', e.target.value)
        }
        label={t('massPayouts.recipientAddress')}
        inputMode="text"
        name="recipientAddress"
        value={massPayoutEditRecordProps?.recipientAddress}
        error={!!errors?.recipientAddress}
        errorText={errors?.recipientAddress?.message}
        disabled={!errors?.recipientAddress}
        onBlur={() => validateCryptoPayout('recipientAddress')}
      />
      {massPayoutEditRecordProps?.tag && (
        <Input
          testId="update-tag-input"
          {...register('tag')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange('tag', e.target.value)
          }
          label={t('massPayouts.tag')}
          inputMode="text"
          name="tag"
          value={massPayoutEditRecordProps?.tag}
          error={!!errors?.tag}
          errorText={errors?.tag?.message}
          disabled={!errors?.tag}
          onBlur={() => validateCryptoPayout('tag')}
        />
      )}
      <Input
        testId="update-reference-input"
        {...register('reference')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleOnChange('reference', e.target.value)
        }
        label={t('massPayouts.reference')}
        inputMode="text"
        name="reference"
        value={massPayoutEditRecordProps?.reference}
        error={!!errors?.reference}
        errorText={errors?.reference?.message}
        disabled={!errors?.reference}
        onBlur={() => validateCryptoPayout('reference')}
      />
    </>
  )
}
